import React from 'react';
import { Box, styled, keyframes } from '@mui/material';
import { Typography } from './Typography';
import Button from './Button';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Action button interface
export interface PageHeaderAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
  loading?: boolean;
}

// Breadcrumb interface
export interface PageHeaderBreadcrumb {
  label: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
}

// Props interface
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: PageHeaderAction[];
  breadcrumbs?: PageHeaderBreadcrumb[];
  showDivider?: boolean;
  children?: React.ReactNode;
  animationDelay?: number; // Delay for animations (in milliseconds)
  disableAnimation?: boolean; // Option to disable animations
  sx?: Record<string, unknown>;
}

// Styled components
const HeaderContainer = styled(Box, {
  shouldForwardProp: prop => !['animationDelay', 'disableAnimation'].includes(prop as string),
})<{ animationDelay?: number; disableAnimation?: boolean }>(
  ({ theme, animationDelay = 0, disableAnimation = false }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    animation: disableAnimation ? 'none' : `${fadeInDown} 0.6s ease-out ${animationDelay}ms both`,
  })
);

const HeaderTop = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const TitleSection = styled(Box, {
  shouldForwardProp: prop => !['animationDelay', 'disableAnimation'].includes(prop as string),
})<{ animationDelay?: number; disableAnimation?: boolean }>(
  ({ animationDelay = 0, disableAnimation = false }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0, // Allow shrinking
    animation: disableAnimation ? 'none' : `${fadeIn} 0.7s ease-out ${animationDelay + 100}ms both`,
  })
);

const ActionsSection = styled(Box, {
  shouldForwardProp: prop => !['animationDelay', 'disableAnimation'].includes(prop as string),
})<{ animationDelay?: number; disableAnimation?: boolean }>(
  ({ theme, animationDelay = 0, disableAnimation = false }) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',
    animation: disableAnimation ? 'none' : `${fadeIn} 0.6s ease-out ${animationDelay + 200}ms both`,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'stretch',
      '& > *': {
        flex: 1,
      },
    },
  })
);

const BreadcrumbsContainer = styled(Box, {
  shouldForwardProp: prop => !['animationDelay', 'disableAnimation'].includes(prop as string),
})<{ animationDelay?: number; disableAnimation?: boolean }>(
  ({ theme, animationDelay = 0, disableAnimation = false }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    animation: disableAnimation ? 'none' : `${fadeIn} 0.8s ease-out ${animationDelay + 50}ms both`,
  })
);

const BreadcrumbItem = styled('span', {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'clickable',
})<{ active?: boolean; clickable?: boolean }>(({ theme, active, clickable }) => ({
  fontSize: '0.875rem',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: active ? 500 : 400,
  cursor: clickable ? 'pointer' : 'default',
  textDecoration: 'none',
  '&:hover': clickable
    ? {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    }
    : {},
}));

const BreadcrumbSeparator = styled('span')(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.disabled,
  margin: theme.spacing(0, 0.5),
}));

const Divider = styled(Box, {
  shouldForwardProp: prop => !['animationDelay', 'disableAnimation'].includes(prop as string),
})<{ animationDelay?: number; disableAnimation?: boolean }>(
  ({ theme, animationDelay = 0, disableAnimation = false }) => ({
    height: 1,
    backgroundColor: theme.palette.divider,
    width: '100%',
    animation: disableAnimation ? 'none' : `${fadeIn} 0.5s ease-out ${animationDelay + 400}ms both`,
  })
);

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions = [],
  breadcrumbs = [],
  showDivider = false,
  children,
  animationDelay = 0,
  disableAnimation = false,
  sx,
}) => {
  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    if (breadcrumbs.length === 0) return null;

    return (
      <BreadcrumbsContainer animationDelay={animationDelay} disableAnimation={disableAnimation}>
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={index}>
            <BreadcrumbItem
              active={breadcrumb.active}
              clickable={!!(breadcrumb.href || breadcrumb.onClick)}
              onClick={breadcrumb.onClick}
              {...(breadcrumb.href && { as: 'a', href: breadcrumb.href })}
            >
              {breadcrumb.label}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}
          </React.Fragment>
        ))}
      </BreadcrumbsContainer>
    );
  };

  // Render action buttons
  const renderActions = () => {
    if (actions.length === 0) return null;

    return (
      <ActionsSection animationDelay={animationDelay} disableAnimation={disableAnimation}>
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant || 'outlined'}
            size={action.size || 'medium'}
            onClick={action.onClick}
            disabled={action.disabled}
            loading={action.loading}
            icon={action.icon}
            iconPosition={action.icon ? 'start' : undefined}
          >
            {action.label}
          </Button>
        ))}
      </ActionsSection>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      mb={3}
      gap={{ xs: 2, sm: 0 }}

    >
      <HeaderContainer sx={{
            width: {
              xs: '320px',   // Mobile devices
              sm: '420px',   // Tablets
              md: '700px',   // Desktops
              lg: '100%',    // Large screens: full width
            },
            maxWidth: '100%', // Prevent overflow
          }} animationDelay={animationDelay} disableAnimation={disableAnimation}>
        <HeaderTop
          >
          <TitleSection animationDelay={animationDelay} disableAnimation={disableAnimation}>
            {renderBreadcrumbs()}
            <Typography
              variant="h4"
              weight="semibold"
              sx={{
                fontSize: '2rem',
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
                animation: disableAnimation
                  ? 'none'
                  : `${fadeIn} 0.9s ease-out ${animationDelay + 150}ms both`,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  fontSize: '1rem',
                  marginTop: 0.5,
                  animation: disableAnimation
                    ? 'none'
                    : `${fadeIn} 1s ease-out ${animationDelay + 200}ms both`,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </TitleSection>
          {renderActions()}
        </HeaderTop>

        {children && (
          <Box
            sx={{
              mt: 1,
              animation: disableAnimation
                ? 'none'
                : `${fadeIn} 0.8s ease-out ${animationDelay + 300}ms both`,
            }}
          >
            {children}
          </Box>
        )}

        {showDivider && (
          <Divider animationDelay={animationDelay} disableAnimation={disableAnimation} />
        )}
      </HeaderContainer>
    </Box>
  );
};

export default PageHeader;
